import React from "react";
import styled from "styled-components";
import { SkeletonBox, SkeletonText, SkeletonCard, SkeletonButton, SkeletonChip } from "./SkeletonBox";

/**
 * DetailPageSkeleton - Non-animated skeleton for Movie/Series detail pages
 * 
 * Matches layout of:
 * - filmPageContent.js (Background: 600px, metadata layout)
 * - Flutter TvDetailsSkeleton structure
 */

var Container = styled.div`
  position: relative;
  width: 100%;
  min-height: 100vh;
  background-color: #121212;
`;

var BackgroundPlaceholder = styled.div`
  width: 100%;
  height: 600px;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  background-color: #151515;
  z-index: 0;
`;

var ShadeOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 600px;
  background: -webkit-linear-gradient(bottom, #121212 0%, rgba(18, 18, 18, 0.8) 30%, rgba(0, 0, 0, 0) 100%);
  background: -moz-linear-gradient(bottom, #121212 0%, rgba(18, 18, 18, 0.8) 30%, rgba(0, 0, 0, 0) 100%);
  background: linear-gradient(to top, #121212 0%, rgba(18, 18, 18, 0.8) 30%, rgba(0, 0, 0, 0) 100%);
  z-index: 1;
`;

var ContentArea = styled.div`
  position: relative;
  z-index: 10;
  padding: 40px;
  padding-top: 300px;
`;

var TitleSection = styled.div`
  margin-bottom: 20px;
`;

var MetadataRow = styled.div`
  display: -webkit-box;
  display: -webkit-flex;
  display: -moz-box;
  display: -ms-flexbox;
  display: flex;
  -webkit-box-orient: horizontal;
  -webkit-box-direction: normal;
  -webkit-flex-direction: row;
  -moz-box-orient: horizontal;
  -moz-box-direction: normal;
  -ms-flex-direction: row;
  flex-direction: row;
  -webkit-box-align: center;
  -webkit-align-items: center;
  -moz-box-align: center;
  -ms-flex-align: center;
  align-items: center;
  margin-top: 12px;
  margin-bottom: 12px;
`;

var MetadataItem = styled.div`
  margin-left: 20px;
  
  &:last-child {
    margin-left: 0;
  }
`;

var GenreRow = styled.div`
  display: -webkit-box;
  display: -webkit-flex;
  display: -moz-box;
  display: -ms-flexbox;
  display: flex;
  -webkit-box-orient: horizontal;
  -webkit-box-direction: normal;
  -webkit-flex-direction: row;
  -moz-box-orient: horizontal;
  -moz-box-direction: normal;
  -ms-flex-direction: row;
  flex-direction: row;
  -webkit-flex-wrap: wrap;
  -ms-flex-wrap: wrap;
  flex-wrap: wrap;
  margin-top: 20px;
  margin-bottom: 20px;
`;

var GenreItem = styled.div`
  margin-left: 10px;
  margin-bottom: 10px;
`;

var DescriptionSection = styled.div`
  max-width: 60%;
  margin-top: 20px;
  margin-bottom: 30px;
`;

var ButtonsRow = styled.div`
  display: -webkit-box;
  display: -webkit-flex;
  display: -moz-box;
  display: -ms-flexbox;
  display: flex;
  -webkit-box-orient: horizontal;
  -webkit-box-direction: normal;
  -webkit-flex-direction: row;
  -moz-box-orient: horizontal;
  -moz-box-direction: normal;
  -ms-flex-direction: row;
  flex-direction: row;
  -webkit-box-align: center;
  -webkit-align-items: center;
  -moz-box-align: center;
  -ms-flex-align: center;
  align-items: center;
  margin-top: 30px;
`;

var ButtonWrapper = styled.div`
  margin-left: 16px;
  
  &:last-child {
    margin-left: 0;
  }
`;

var RelatedSection = styled.div`
  margin-top: 60px;
  padding-bottom: 60px;
`;

var RelatedTitle = styled.div`
  height: 27px;
  width: 200px;
  margin-bottom: 22px;
  margin-right: 40px;
  background-color: #262626;
  -webkit-border-radius: 4px;
  -moz-border-radius: 4px;
  border-radius: 4px;
`;

var CardsContainer = styled.div`
  display: -webkit-box;
  display: -webkit-flex;
  display: -moz-box;
  display: -ms-flexbox;
  display: flex;
  -webkit-box-orient: horizontal;
  -webkit-box-direction: normal;
  -webkit-flex-direction: row;
  -moz-box-orient: horizontal;
  -moz-box-direction: normal;
  -ms-flex-direction: row;
  flex-direction: row;
  overflow: hidden;
`;

var CardWrapper = styled.div`
  margin-left: 12px;
  -webkit-flex-shrink: 0;
  -moz-flex-shrink: 0;
  -ms-flex-negative: 0;
  flex-shrink: 0;
  
  &:last-child {
    margin-left: 0;
  }
`;

/**
 * Hero Content Skeleton
 */
function HeroContentSkeleton() {
  return (
    <ContentArea>
      <TitleSection>
        {/* Main Title */}
        <SkeletonText width={400} size="xl" style={{ marginBottom: 12 }} />
        
        {/* English Title */}
        <SkeletonText width={280} size="lg" />
      </TitleSection>
      
      {/* Metadata Row: Year | Country | Duration */}
      <MetadataRow>
        <MetadataItem>
          <SkeletonText width={60} size="md" />
        </MetadataItem>
        <MetadataItem>
          <SkeletonBox width={2} height={20} borderRadius={1} />
        </MetadataItem>
        <MetadataItem>
          <SkeletonText width={80} size="md" />
        </MetadataItem>
        <MetadataItem>
          <SkeletonBox width={2} height={20} borderRadius={1} />
        </MetadataItem>
        <MetadataItem>
          <SkeletonText width={100} size="md" />
        </MetadataItem>
      </MetadataRow>
      
      {/* Rating Row: IMDB | Likes */}
      <MetadataRow>
        <MetadataItem>
          <SkeletonBox width={50} height={24} borderRadius={4} />
        </MetadataItem>
        <MetadataItem>
          <SkeletonText width={40} size="md" />
        </MetadataItem>
        <MetadataItem>
          <SkeletonBox width={24} height={24} circle />
        </MetadataItem>
        <MetadataItem>
          <SkeletonText width={50} size="md" />
        </MetadataItem>
      </MetadataRow>
      
      {/* Genre Chips */}
      <GenreRow>
        <GenreItem>
          <SkeletonChip width={80} />
        </GenreItem>
        <GenreItem>
          <SkeletonChip width={100} />
        </GenreItem>
        <GenreItem>
          <SkeletonChip width={90} />
        </GenreItem>
      </GenreRow>
      
      {/* Description */}
      <DescriptionSection>
        <SkeletonText width="100%" size="md" style={{ marginBottom: 8 }} />
        <SkeletonText width="100%" size="md" style={{ marginBottom: 8 }} />
        <SkeletonText width="70%" size="md" />
      </DescriptionSection>
      
      {/* Action Buttons */}
      <ButtonsRow>
        <ButtonWrapper>
          <SkeletonButton variant="primary" />
        </ButtonWrapper>
        <ButtonWrapper>
          <SkeletonButton variant="primary" />
        </ButtonWrapper>
        <ButtonWrapper>
          <SkeletonButton variant="icon" />
        </ButtonWrapper>
        <ButtonWrapper>
          <SkeletonButton variant="icon" />
        </ButtonWrapper>
        <ButtonWrapper>
          <SkeletonButton variant="icon" />
        </ButtonWrapper>
      </ButtonsRow>
    </ContentArea>
  );
}

/**
 * Related Content Skeleton
 */
function RelatedContentSkeleton({ cardCount = 6 }) {
  var cards = [];
  for (var i = 0; i < cardCount; i++) {
    cards.push(
      <CardWrapper key={"related_" + i}>
        <SkeletonCard type="portrait" />
      </CardWrapper>
    );
  }
  
  return (
    <RelatedSection>
      <RelatedTitle />
      <CardsContainer>
        {cards}
      </CardsContainer>
    </RelatedSection>
  );
}

/**
 * Artist Slider Skeleton
 */
function ArtistSliderSkeleton({ count = 6 }) {
  var artists = [];
  for (var i = 0; i < count; i++) {
    artists.push(
      <CardWrapper key={"artist_" + i}>
        <SkeletonCard type="artist" />
      </CardWrapper>
    );
  }
  
  return (
    <RelatedSection>
      <RelatedTitle />
      <CardsContainer>
        {artists}
      </CardsContainer>
    </RelatedSection>
  );
}

/**
 * DetailPageSkeleton - Main export
 */
function DetailPageSkeleton({ showRelated = true, showArtists = true }) {
  return (
    <Container>
      <BackgroundPlaceholder />
      <ShadeOverlay />
      
      <HeroContentSkeleton />
      
      {showArtists && <ArtistSliderSkeleton count={6} />}
      {showRelated && <RelatedContentSkeleton cardCount={6} />}
    </Container>
  );
}

export { HeroContentSkeleton, RelatedContentSkeleton, ArtistSliderSkeleton };
export default DetailPageSkeleton;
